import React, { useState } from 'react';
import { Container, Button, Modal, Badge, Form, Row } from 'react-bootstrap';
import CommonTable from '../table/CommonTable';
import moment from 'moment';
import { Edit } from '@mui/icons-material';
import OrderIcon from '@mui/icons-material/ReceiptLong';
import { FormField } from '../formFields/FormField';
import toast from 'react-hot-toast';
import { postDatatoServer } from '../../utils/apiBase';
import ContextHelper from '../../ContextHooks/ContextHelper';

const SUBSCRIPTION_DATA = {
  current: [
    {
      id: '147289',
      orderDateTime: '2024-07-09T11:27:20',
      startDate: '2024-07-09',
      name: 'Agent01',
      email: 'agentA@example.com',
      renewalDate: '2025-07-09',
      plan: 'Annually',
    },
  ],
  upcoming: [
    {
      id: '157200',
      orderDateTime: '2024-11-15T09:45:00',
      startDate: '2025-08-15',
      name: 'Agent01',
      email: 'agentA@example.com',
      renewalDate: '2025-11-15',
      plan: 'Quarterly',
    },
  ],
  expired: [
    {
      id: '110442',
      orderDateTime: '2023-06-23T13:03:45',
      startDate: '2023-06-23',
      name: 'Agent01',
      email: 'agentA@example.com',
      renewalDate: '2024-06-23',
      plan: 'Monthly',
    },
    {
      id: '107899',
      orderDateTime: '2023-03-12T16:12:30',
      startDate: '2023-03-12',
      name: 'Agent01',
      email: 'agentA@example.com',
      renewalDate: '2024-03-12',
      plan: 'Monthly',
    },
  ],
};

const PLAN_DURATIONS = {
  Monthly: 1,
  Quarterly: 3,
  Annually: 12,
  Custom: 0,
};

function AdminPanel() {
  const [showManagePlanModal, setShowManagePlanModal] = useState(false);
  const [showAddPlanModal, setShowAddPlanModal] = useState(false);
  const [currentAgentId, setCurrentAgentId] = useState(null);
  const [filterStatus, setFilterStatus] = useState('All');
  const [formData, setFormData] = useState({
    email: '',
    userName: '',
    endDate: '',
    startDate: '',
    plan: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [validated, setValidated] = useState(false);
  const { currentUser } = ContextHelper();

  const handleManagePlan = (subscription) => {
    setCurrentAgentId(subscription.id);
    setFormData({
      email: subscription.email,
      userName: subscription.name,
      startDate: subscription.startDate,
      endDate: subscription.endDate,
      plan: subscription.plan,
    });
    setErrors({});
    setValidated(false);
    setShowManagePlanModal(true);
  };

  const handleAddPlan = () => {
    setFormData({
      email: '',
      userName: '',
      startDate: '',
      endDate: '',
      plan: '',
    });
    setErrors({});
    setValidated(false);
    setShowAddPlanModal(true);
  };

  const calculateEndDate = (startDate, plan) => {
    if (!startDate || plan === 'Custom') return '';

    const duration = PLAN_DURATIONS[plan];
    if (!duration) return '';

    return moment(startDate).add(duration, 'months').format('YYYY-MM-DD');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => {
      const newData = {
        ...prevData,
        [name]: value.trim(),
      };

      if (name === 'plan') {
        const today = moment().format('YYYY-MM-DD');
        newData.startDate = newData.startDate || today;

        if (value !== 'Custom' && newData.startDate) {
          newData.endDate = calculateEndDate(newData.startDate, value);
        }
      }

      if (name === 'startDate' && prevData.plan && prevData.plan !== 'Custom') {
        newData.endDate = calculateEndDate(value, prevData.plan);
      }

      return newData;
    });

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const currentDate = moment().startOf('day');

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.userName) {
      newErrors.userName = 'Username is required';
    } else if (formData.userName.length < 3) {
      newErrors.userName = 'Username must be at least 3 characters long';
    }

    if (!formData.plan) {
      newErrors.plan = 'Please select plan';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Please select start date';
    } else {
      const startDate = moment(formData.startDate);
      if (startDate.isBefore(currentDate)) {
        newErrors.startDate = 'Start date must be greater than or equal to current date';
      }
    }

    if (!formData.endDate) {
      newErrors.endDate = 'Please select end date';
    } else {
      const startDate = moment(formData.startDate);
      const endDate = moment(formData.endDate);

      if (endDate.isSameOrBefore(startDate)) {
        newErrors.endDate = 'End date must be after start date';
      }

      if (formData.plan && formData.plan !== 'Custom') {
        const expectedEndDate = calculateEndDate(formData.startDate, formData.plan);
        if (formData.endDate !== expectedEndDate) {
          newErrors.endDate = `End date must be ${moment(expectedEndDate).format('DD-MMM-YYYY')} for ${
            formData.plan
          } plan`;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidated(true);

    if (validateForm()) {
      setLoading(true);
      try {
        const response = await postDatatoServer({
          end_point: 'addOrUpdatePlan',
          body: { ...formData },
          call_back: (res) => {
            if (res.response.status) {
              toast.success(res.response.message);
              setShowAddPlanModal(false);
              setShowManagePlanModal(false);
              setFormData({ email: '', userName: '', plan: '', startDate: '', endDate: '' });
              setValidated(false);
            } else {
              toast.error(res.response.message);
            }
          },
          props: { header: true, token: currentUser?.token },
        });
      } catch (error) {
        console.error('Error submitting plan:', error);
        toast.error('An error occurred. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const PlanModal = ({ show, onHide, title, isEdit }) => {
    const today = moment().format('YYYY-MM-DD');

    return (
      <Modal show={show} onHide={onHide} size="lg" animation={false} centered>
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form noValidate validated={validated} onSubmit={handleSubmit}>
            <Row>
              <FormField
                label="Agent Username"
                name="userName"
                value={formData?.userName?.replace(/\s+/g, '')}
                onChange={handleChange}
                error={errors.userName}
                required
                col={6}
              />
              <FormField
                label="Agent Email"
                name="email"
                value={formData?.email?.replace(/\s+/g, '')}
                onChange={handleChange}
                error={errors.email}
                required
                col={6}
              />

              <FormField
                col={6}
                label="Plan"
                name="plan"
                value={formData.plan}
                onChange={handleChange}
                error={errors.plan}
                required
                options={['Monthly', 'Quarterly', 'Annually', 'Custom']}
              />
              <FormField
                label="Start Date"
                name="startDate"
                value={formData?.startDate}
                onChange={handleChange}
                error={errors.startDate}
                required
                type="date"
                min={today}
                col={6}
                disabled={['Annually', 'Quarterly', 'Monthly'].includes(formData.plan)}
              />

              <FormField
                label="End Date"
                name="endDate"
                value={formData?.endDate}
                onChange={handleChange}
                error={errors.endDate}
                required
                type="date"
                min={formData.startDate || today}
                disabled={formData.plan && formData.plan !== 'Custom'}
                col={6}
              />
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button className="login-btn base-color-bg text-white px-4" onClick={handleSubmit} disabled={loading}>
            {isEdit ? 'Update Plan' : 'Add Plan'}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  const handleInvoice = (id) => {
    alert(`Invoice for Agent ID #${id}`);
  };

  const filteredData = Object.values(SUBSCRIPTION_DATA)
    .flat()
    .filter((subscription) => filterStatus === 'All' || subscription.status === filterStatus);

  const getStatusByDates = (startDate, renewalDate) => {
    const currentDate = moment();

    if (moment(startDate).isAfter(currentDate)) {
      return 'Upcoming';
    } else if (moment(renewalDate).isBefore(currentDate) || moment(renewalDate).isSame(currentDate)) {
      return 'Expired';
    } else {
      return 'Current';
    }
  };

  const columns = [
    {
      accessor: 'id',
      label: 'Order ID',
      render: (value) => <span>#{value}</span>,
    },
    {
      accessor: 'orderDateTime',
      label: 'Order Date',
      render: (value) => moment(value).format('DD-MMM-YYYY HH:mm'),
    },
    {
      accessor: 'startDate',
      label: 'Start Date',
      render: (value) => moment(value).format('DD-MMM-YYYY'),
    },
    {
      accessor: 'renewalDate',
      label: 'Renewal Date',
      render: (value) => moment(value).format('DD-MMM-YYYY'),
    },
    { accessor: 'email', label: 'Agent Email' },
    { accessor: 'name', label: 'Agent Username' },
    { accessor: 'plan', label: 'Plan' },
    {
      accessor: 'status',
      label: 'Status',
      render: (value, row) => {
        const status = getStatusByDates(row.startDate, row.renewalDate);
        return (
          <Badge
            className={`fs-12 ${status === 'Expired' ? 'text-white' : ''}`}
            bg={
              status === 'Expired' ? 'danger' : status === 'Upcoming' ? 'warning' : 'success' // Active status
            }
          >
            {status}
          </Badge>
        );
      },
    },
    {
      accessor: 'actions',
      label: 'Actions',
      render: (value, row) => (
        <div>
          <Button variant="success" size="sm" title="Edit" onClick={() => handleManagePlan(row)} className="me-2">
            <Edit />
          </Button>
          <Button variant="info" size="sm" className="text-white" title="Invoice" onClick={() => handleInvoice(row.id)}>
            <OrderIcon />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Container fluid className="px-0">
      <div className="bg-white p-4 min-vh-100 shadow-sm">
        <div className="d-flex justify-content-between mb-2">
          <h3 className="base-color">Subscription Management</h3>
          <Button className="login-btn base-color-bg text-white px-4" onClick={handleAddPlan}>
            Add Plan
          </Button>
        </div>

        <div className="mb-3 position-absolute">
          <Form.Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="All">All</option>
            <option value="Current">Current</option>
            <option value="Upcoming">Upcoming</option>
            <option value="Expired">Expired</option>
          </Form.Select>
        </div>

        <CommonTable data={filteredData} columns={columns} loading={loading} />

        <PlanModal
          show={showAddPlanModal}
          onHide={() => !loading && setShowAddPlanModal(false)}
          title="Add New Plan"
          isEdit={false}
        />

        <PlanModal
          show={showManagePlanModal}
          onHide={() => !loading && setShowManagePlanModal(false)}
          title={`Manage Plan for Agent ID #${currentAgentId}`}
          isEdit={true}
        />
      </div>
    </Container>
  );
}

export default AdminPanel;
